import { useState } from "react";
import { Mail, MapPin, Phone, Send, MessageCircle, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Lütfen zorunlu alanları doldurun.");
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 700));
    toast.success("Mesajınız gönderildi!", { description: "Size 24 saat içinde dönüş yapacağız." });
    setFormData({ name: "", email: "", subject: "", message: "" });
    setSubmitting(false);
  };

  const infos = [
    { icon: Mail, label: "E-posta", value: "destek@drsoft.com.tr", sub: "İş günlerinde 2 saat içinde yanıt." },
    { icon: Phone, label: "Telefon", value: "+90 212 555 0123", sub: "Müşteriler için özel 7/24 hat." },
    { icon: MapPin, label: "Merkez", value: "İstanbul, Türkiye", sub: "Ankara ve Amsterdam ofisleri." },
    { icon: Clock, label: "Destek saatleri", value: "7/24/365", sub: "Ortalama ilk yanıt: 1d 48s." },
  ];

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-50 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-96 radial-glow-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10 lg:pt-24 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-5">İletişim</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Hızlı ve güvenli bir şey
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">{" "}birlikte inşa edelim.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl mx-auto">
            Paketler, mimari veya uyumluluk hakkında soru mu var? Ekibimiz genellikle bir saat
            içinde yanıt verir.
          </p>
        </div>
      </section>

      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
          <form data-testid="contact-form" onSubmit={handleSubmit} className="lg:col-span-3 glass rounded-3xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <MessageCircle size={20} className="text-blue-400" strokeWidth={1.6} />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight">Mesaj gönder</h2>
                <p className="text-sm text-zinc-500">24 saat içinde dönüş yapacağız.</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-zinc-300 text-xs font-semibold uppercase tracking-[0.14em] mb-2 block">Ad Soyad *</Label>
                <Input id="name" name="name" data-testid="contact-input-name" value={formData.name} onChange={handleChange} placeholder="Ayşe Yılmaz" className="bg-white/5 border-white/10 h-12 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20" required />
              </div>
              <div>
                <Label htmlFor="email" className="text-zinc-300 text-xs font-semibold uppercase tracking-[0.14em] mb-2 block">E-posta *</Label>
                <Input id="email" name="email" type="email" data-testid="contact-input-email" value={formData.email} onChange={handleChange} placeholder="ayse@sirket.com" className="bg-white/5 border-white/10 h-12 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20" required />
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="subject" className="text-zinc-300 text-xs font-semibold uppercase tracking-[0.14em] mb-2 block">Konu</Label>
              <Input id="subject" name="subject" data-testid="contact-input-subject" value={formData.subject} onChange={handleChange} placeholder="Nasıl yardımcı olabiliriz?" className="bg-white/5 border-white/10 h-12 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20" />
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="text-zinc-300 text-xs font-semibold uppercase tracking-[0.14em] mb-2 block">Mesaj *</Label>
              <Textarea id="message" name="message" data-testid="contact-input-message" value={formData.message} onChange={handleChange} placeholder="Projenizden veya sorunuzdan bahsedin..." rows={6} className="bg-white/5 border-white/10 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20 resize-none" required />
            </div>

            <button type="submit" data-testid="contact-submit-button" disabled={submitting} className="btn-shine mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed px-7 py-4 text-sm font-semibold text-white shadow-[0_0_30px_rgba(0,122,255,0.3)] transition-all">
              {submitting ? "Gönderiliyor..." : (<>Mesajı gönder <Send size={15} /></>)}
            </button>

            <p className="mt-4 text-xs text-zinc-500 text-center">
              Formu göndererek gizlilik politikamızı kabul etmiş olursunuz. Verileriniz paylaşılmaz.
            </p>
          </form>

          <aside className="lg:col-span-2 space-y-4">
            {infos.map((info, i) => (
              <div key={info.label} data-testid={`contact-info-${i}`} className="card-hover glass rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <info.icon size={18} className="text-blue-400" strokeWidth={1.6} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1">{info.label}</p>
                    <p className="font-heading text-lg font-semibold">{info.value}</p>
                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{info.sub}</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="relative rounded-2xl overflow-hidden border border-white/10 h-64">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-[#12141D] to-[#0A0B10]" />
              <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 60% 40%, rgba(0,122,255,0.4), transparent 45%), radial-gradient(circle at 30% 70%, rgba(59,130,246,0.3), transparent 40%)" }} />
              <div className="absolute inset-0 grid-overlay opacity-50" />
              <div className="relative h-full p-6 flex flex-col justify-end">
                <MapPin size={22} className="text-blue-400 mb-3" />
                <p className="font-heading text-lg font-semibold">İstanbul Merkez</p>
                <p className="text-sm text-zinc-400">Maslak · Sarıyer · 34485</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
